import React, { useState, useEffect } from 'react';
import { useLazyQuery, useLazyCommand, useEvent } from '@soap/modules';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Card } from '../../ui/card';

interface FormData {
  C181_AssessmentId: string;
  C181_AssessmentTitle: string;
  C181_Description: string;
}

export function EditAssessmentTitle({ assessmentId }: { assessmentId: string }) {
  const [getFormData] = useLazyQuery("C182v1_GetC181FormData");
  const [submitCommand] = useLazyCommand("C181v1_EditAssessmentTitle");
  const [formData, setFormData] = useState<FormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ title?: string; description?: string }>({});

  useEffect(() => {
    if (assessmentId) {
      getFormData({
        C182_AssessmentId: assessmentId
      });
    }
  }, [assessmentId]);

  useEvent({
    eventName: "E151v1_GotC181FormData",
    onEventReceived: (event: any) => {
      // The event contains e000_FieldData with flattened field structure (camelCase in browser)
      const fieldData = event.e000_FieldData || [];
      
      // Find the C181 fields
      const assessmentIdField = fieldData.find((field: any) => field.name === 'c181_AssessmentId');
      const titleField = fieldData.find((field: any) => field.name === 'c181_AssessmentTitle');
      const descriptionField = fieldData.find((field: any) => field.name === 'c181_Description');
      
      setFormData({
        C181_AssessmentId: assessmentIdField?.initialValue || assessmentId,
        C181_AssessmentTitle: titleField?.initialValue || '',
        C181_Description: descriptionField?.initialValue || ''
      });
      setLoading(false);
    }
  }, []);

  useEvent({
    eventName: "E149v1_AssessmentUpdated",
    onEventReceived: () => {
      setSaving(false);
    }
  }, []);

  const validate = (): boolean => {
    const newErrors: { title?: string; description?: string } = {};
    
    if (!formData?.C181_AssessmentTitle?.trim()) {
      newErrors.title = "Title is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData || !validate()) {
      return;
    }

    setSaving(true);
    submitCommand({
      $type: "C181v1_EditAssessmentTitle",
      C181_AssessmentId: formData.C181_AssessmentId,
      C181_AssessmentTitle: formData.C181_AssessmentTitle,
      C181_Description: formData.C181_Description
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-action-blue"></div>
      </div>
    );
  }

  if (!formData) return null;

  return (
    <Card className="p-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <Label htmlFor="title" className="text-sm font-medium">
            Title <span className="text-destructive-action">*</span>
          </Label>
           <Input
             id="title"
             value={formData.C181_AssessmentTitle}
             onChange={(e) => setFormData({ ...formData, C181_AssessmentTitle: e.target.value })}
             placeholder="Enter assessment title"
             className={errors.title ? 'border-destructive-action' : ''}
           />
          {errors.title && (
            <p className="text-sm text-destructive-action mt-1">{errors.title}</p>
          )}
        </div>

        <div>
          <Label htmlFor="description" className="text-sm font-medium">
            Description
          </Label>
          <textarea
            id="description"
            value={formData.C181_Description}
            onChange={(e) => setFormData({ ...formData, C181_Description: e.target.value })}
            placeholder="Enter assessment description"
            rows={5}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-action-blue"
          />
          {errors.description && (
            <p className="text-sm text-destructive-action mt-1">{errors.description}</p>
          )}
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

